import { ShieldAlert } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from "./ParsleeAuthProvider";

interface StaffGateProps {
  children: React.ReactNode;
}

/**
 * Gate for the Parslee-staff-only admin dashboard.
 *
 * Renders children only when the signed-in user is a Parslee staffer (the
 * m365-sourced `isParsleeStaff` signal). A non-staff user — including a valid
 * customer-org Owner — who reaches a gated route lands on an access-denied
 * state, NEVER the dashboard. This is defense-in-depth on top of the server
 * gate (every admin endpoint enforces Parslee-staff authorization): the section
 * being unreachable in the UI is the client half; the backend 403 is the half
 * that actually protects the data.
 *
 * `isParsleeStaff` is fail-closed (false when the signal is absent), so an
 * old/not-yet-deployed m365 simply leaves the dashboard behind access-denied.
 */
export function StaffGate({ children }: StaffGateProps) {
  const { isLoading, isParsleeStaff } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spinner size={32} label="Loading..." />
      </div>
    );
  }

  if (!isParsleeStaff) {
    return (
      <div
        role="alert"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}
      >
        <div style={{ maxWidth: 420, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <ShieldAlert className="h-10 w-10 text-destructive" />
          <h1 className="text-2xl font-bold text-foreground">Access denied</h1>
          <p className="text-sm text-muted-foreground">
            This is a Parslee-internal tool. Your account does not have access
            to this dashboard.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
